/**
 * AddGatewayOrgDialog — org-scoped add gateway: claim code → select property → link.
 * Reuses scan/manual flow from AddGatewayDialog; adds property selector step.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Camera, Keyboard, CheckCircle2, AlertCircle } from 'lucide-react';
import { linkGatewayToProperty } from '@/services/gateway';
import { useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { useGatewayProfiles } from '@/hooks/useGatewayProfiles';

export interface AddGatewayOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Org default profile — shown as note when no profile selected */
  defaultProfileId?: string;
  defaultProfileName?: string;
}

type Step = 'scan' | 'property' | 'confirm' | 'linking' | 'success' | 'error';

function parseClaimCode(raw: string): string | null {
  const qrMatch = raw.match(/^wt:\/\/claim\/(.+)$/);
  if (qrMatch) return qrMatch[1];
  const trimmed = raw.trim();
  if (/^WT-[A-Za-z0-9]+$/.test(trimmed)) return trimmed;
  return null;
}

export function AddGatewayOrgDialog({
  open,
  onOpenChange,
  defaultProfileId,
  defaultProfileName,
}: AddGatewayOrgDialogProps) {
  const queryClient = useQueryClient();
  const { properties } = useApp();
  const { profiles } = useGatewayProfiles();
  const [step, setStep] = useState<Step>('scan');
  const [claimCode, setClaimCode] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [useManual, setUseManual] = useState(false);
  const [profileId, setProfileId] = useState<string>('');
  const [propertyId, setPropertyId] = useState<string>('');
  const [errorMsg, setErrorMsg] = useState('');
  const [cameraError, setCameraError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<unknown>(null);

  const selectedProfile = profileId ? profiles.find((p) => p.id === profileId) : null;
  const autoCreateProperty = selectedProfile?.auto_create_property ?? false;
  const propertyOptional = autoCreateProperty;

  useEffect(() => {
    if (open) {
      setStep('scan');
      setClaimCode('');
      setManualInput('');
      setUseManual(false);
      setProfileId('');
      setPropertyId('');
      setErrorMsg('');
      setCameraError(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || step !== 'scan' || useManual || cameraError) return;
    let scanner: { stop: () => void; destroy: () => void } | null = null;
    let cancelled = false;
    async function initScanner() {
      try {
        const QrScanner = (await import('qr-scanner')).default;
        if (cancelled || !videoRef.current) return;
        scanner = new QrScanner(
          videoRef.current,
          (result: { data: string }) => {
            const code = parseClaimCode(result.data);
            if (code) {
              setClaimCode(code);
              setStep('property');
              scanner?.stop();
            }
          },
          { preferredCamera: 'environment', highlightScanRegion: true, highlightCodeOutline: true }
        );
        scannerRef.current = scanner;
        await scanner.start();
      } catch {
        if (!cancelled) setCameraError(true);
      }
    }
    initScanner();
    return () => {
      cancelled = true;
      scanner?.stop();
      scanner?.destroy();
      scannerRef.current = null;
    };
  }, [open, step, useManual, cameraError]);

  const handleManualSubmit = useCallback(() => {
    const code = parseClaimCode(manualInput);
    if (code) {
      setClaimCode(code);
      setStep('property');
    } else {
      setErrorMsg('Invalid claim code. Expected format: WT-XXXXXX');
    }
  }, [manualInput]);

  const handleLink = useCallback(async () => {
    if (!propertyOptional && !propertyId) {
      setErrorMsg('Select a property');
      return;
    }
    setStep('linking');
    setErrorMsg('');
    try {
      await linkGatewayToProperty(
        claimCode,
        propertyId || null,
        profileId || undefined
      );
      queryClient.invalidateQueries({ queryKey: ['gateway-devices'] });
      queryClient.invalidateQueries({ queryKey: ['gateway-devices-fleet'] });
      setStep('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to link gateway');
      setStep('error');
    }
  }, [claimCode, propertyId, profileId, propertyOptional, queryClient]);

  const selectedPropertyName = propertyId
    ? properties.find((p) => p.id === propertyId)?.name ?? ''
    : autoCreateProperty
      ? 'Will create on first boot'
      : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-wt-bg-secondary border-border">
        <DialogHeader>
          <DialogTitle className="text-wt-text-primary">
            {step === 'success' ? 'Gateway Linked' : 'Add Gateway'}
          </DialogTitle>
        </DialogHeader>

        {step === 'scan' && (
          <div className="space-y-4">
            {!useManual && !cameraError ? (
              <div className="space-y-3">
                <div className="relative rounded-lg overflow-hidden bg-black aspect-square max-h-64 mx-auto">
                  <video ref={videoRef} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 border-2 border-dashed border-wt-green-primary/30 m-8 rounded-lg" />
                </div>
                <p className="text-xs text-wt-text-secondary text-center">
                  Point camera at the QR code on the gateway
                </p>
                <Button variant="outline" size="sm" onClick={() => setUseManual(true)} className="w-full">
                  <Keyboard className="w-3.5 h-3.5 mr-1.5" />
                  Enter Code Manually
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {cameraError && (
                  <p className="text-xs text-wt-text-secondary text-center">
                    Camera not available. Enter the claim code manually.
                  </p>
                )}
                <Input
                  value={manualInput}
                  onChange={(e) => { setManualInput(e.target.value); setErrorMsg(''); }}
                  placeholder="WT-XXXXXX"
                  className="font-mono-numeric"
                  onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
                />
                {errorMsg && <p className="text-xs text-wt-red-primary">{errorMsg}</p>}
                <div className="flex gap-2">
                  {!cameraError && (
                    <Button variant="outline" size="sm" onClick={() => setUseManual(false)} className="flex-1">
                      <Camera className="w-3.5 h-3.5 mr-1.5" />
                      Use Camera
                    </Button>
                  )}
                  <Button size="sm" onClick={handleManualSubmit} className="flex-1">
                    Continue
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'property' && (
          <div className="space-y-4">
            <div className="rounded-lg bg-wt-bg-tertiary p-3 text-sm">
              <span className="text-wt-text-secondary">Claim code </span>
              <span className="text-wt-text-primary font-mono-numeric">{claimCode}</span>
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-secondary">Profile (optional)</Label>
              <Select value={profileId || 'none'} onValueChange={(v) => setProfileId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-wt-bg-tertiary border-border">
                  <SelectValue placeholder="No profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No profile</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                      {p.auto_create_property ? ' (zero-touch)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!profileId && defaultProfileName && (
                <p className="text-xs text-wt-text-secondary">
                  Default profile &quot;{defaultProfileName}&quot; will be applied.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-wt-text-secondary">
                Assign to property {propertyOptional ? '(optional for zero-touch)' : ''}
              </Label>
              <Select value={propertyId || 'none'} onValueChange={(v) => setPropertyId(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-wt-bg-tertiary border-border">
                  <SelectValue placeholder={propertyOptional ? 'Leave unassigned for auto-create' : 'Select property'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{propertyOptional ? 'Create on first boot' : '—'}</SelectItem>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {autoCreateProperty && (
                <p className="text-xs text-wt-amber-primary">
                  Gateway will create a property on first connection if none is selected.
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => { setStep('scan'); setClaimCode(''); }}>
                Back
              </Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={!propertyOptional && !propertyId}
              >
                Continue
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-4">
            <div className="rounded-lg bg-wt-bg-tertiary p-4 space-y-2">
              {selectedProfile && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-wt-text-secondary">Profile</span>
                  <span className="text-wt-text-primary font-medium">{selectedProfile.name}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-wt-text-secondary">Property</span>
                <span className="text-wt-text-primary font-medium">{selectedPropertyName}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-wt-text-secondary">Claim Code</span>
                <span className="text-wt-text-primary font-mono-numeric">{claimCode}</span>
              </div>
            </div>
            <p className="text-sm text-wt-text-secondary text-center">
              {propertyId
                ? `Link this gateway to ${selectedPropertyName}?`
                : autoCreateProperty
                  ? 'Link gateway with zero-touch profile. A property will be created on first boot.'
                  : 'Link this gateway?'}
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setStep('property')}>
                Back
              </Button>
              <Button onClick={handleLink}>Link Gateway</Button>
            </DialogFooter>
          </div>
        )}

        {step === 'linking' && (
          <div className="py-8 flex flex-col items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
            <p className="text-sm text-wt-text-secondary">Linking gateway...</p>
          </div>
        )}

        {step === 'success' && (
          <div className="py-4 space-y-4">
            <div className="flex flex-col items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-wt-green-primary" />
              <p className="text-sm text-wt-text-primary text-center">
                Gateway linked. Power on the device to begin registration.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </div>
        )}

        {step === 'error' && (
          <div className="py-4 space-y-4">
            <div className="flex flex-col items-center gap-3">
              <AlertCircle className="w-8 h-8 text-wt-red-primary" />
              <p className="text-sm text-wt-red-primary text-center">{errorMsg}</p>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setStep('scan')}>Try Again</Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
